import { createSelector } from '@reduxjs/toolkit';

import { RootState } from '@/core/interfaces/store';
import {
  ReportReadStatus,
  ReportReadSwitchStatus,
  ReportSourceType,
} from '@/core/interfaces/common';
import { getAllAppliedFiltersSelector } from '@/core/store/reducers/filtersSlice';

import { reportMatchesSourceCredibility } from '@/features/Reports/helpers';
import { Report, ReportsState, SourceCredibility } from '@/features/Reports/interfaces';

import { transformAppliedFiltersToPostData } from '@/utils/filters';

const reportsSelector = (state: RootState): ReportsState => state.reports;

const filterReportBySourceCredibility = (
  report: Report,
  sourceCredibility?: Array<SourceCredibility>
): boolean => {
  if (!sourceCredibility?.length || report.reportSourceType !== ReportSourceType.LOOKOUT) {
    return true;
  }

  return reportMatchesSourceCredibility(report.source, sourceCredibility);
};

export const getActiveReportDataSelector = createSelector(
  [reportsSelector],
  state => state.activeReportData
);

export const isReportDetailsOpenSelector = createSelector(
  [reportsSelector],
  state => state.activeReportData !== null
);

export const getHorizonReportsListSelector = createSelector(
  [reportsSelector],
  state => state.horizonReportsList
);

export const getLookoutReportsListSelector = createSelector(
  [reportsSelector],
  state => state.lookoutReportsList
);

export const getReportsReadSwitchStatusSelector = createSelector(
  [reportsSelector],
  state => state.readSwitchStatus
);

export const isReportsListLoadingSelector = createSelector(
  [reportsSelector],
  state => state.horizonReportsListLoading || state.lookoutReportsListLoading
);

export const isMulticolumnReportsLoadingSelector = createSelector(
  [reportsSelector, (_state, focusId: number) => focusId],
  (state, focusId) =>
    !!state.multicolumnHorizonLoading[focusId] || !!state.multicolumnLookoutLoading[focusId]
);

export const getReportsListSelector = createSelector(
  [
    reportsSelector,
    getReportsReadSwitchStatusSelector,
    getActiveReportDataSelector,
    getAllAppliedFiltersSelector,
  ],
  (state, reportsReadSwitchStatus, activeReportData, appliedFilters) => {
    const { sourceCredibility } = transformAppliedFiltersToPostData(appliedFilters);

    return [...state.horizonReportsList, ...state.lookoutReportsList]
      .filter(report => {
        if (!filterReportBySourceCredibility(report, sourceCredibility)) {
          return false;
        }

        if (reportsReadSwitchStatus === ReportReadSwitchStatus.ALL) {
          return true;
        }

        if (report.reportId === activeReportData?.reportId) {
          return true;
        }

        return report.readStatus === ReportReadStatus.UNREAD;
      })
      .sort((a, b) => {
        const dateAField =
          a.reportSourceType === ReportSourceType.LOOKOUT
            ? a.modifiedDatetime
            : a.publishedDatetime;
        const dateBField =
          b.reportSourceType === ReportSourceType.LOOKOUT
            ? b.modifiedDatetime
            : b.publishedDatetime;

        const dateA = new Date(dateAField).getTime();
        const dateB = new Date(dateBField).getTime();

        return dateB - dateA;
      });
  }
);

export const getMulticolumnsListSelector = createSelector(
  reportsSelector,
  state => state.multicolumnsList
);

export const getHorizonReportsListForMulticolumnSelector = createSelector(
  [reportsSelector, (_state, focusId: number) => focusId],
  (state, focusId: number) =>
    state.multicolumnsList.find(column => column.id === focusId)?.horizonReports || []
);

export const getLookoutReportsListForMulticolumnSelector = createSelector(
  [reportsSelector, (_state, focusId: number) => focusId],
  (state, focusId: number) =>
    state.multicolumnsList.find(column => column.id === focusId)?.lookoutReports || []
);

export const getMulticolumnReportsSelector = createSelector(
  [reportsSelector, (_state, focusId: number) => focusId, getActiveReportDataSelector],
  (state, focusId: number, activeReportData) => {
    const multicolumn = state.multicolumnsList.find(column => column.id === focusId);

    const horizonReports = multicolumn?.horizonReports || [];
    const lookoutReports = multicolumn?.lookoutReports || [];
    const sourceCredibility = multicolumn?.filters
      ? transformAppliedFiltersToPostData(multicolumn.filters).sourceCredibility
      : undefined;

    return [...horizonReports, ...lookoutReports]
      .filter(report => {
        if (!filterReportBySourceCredibility(report, sourceCredibility)) {
          return false;
        }

        if (multicolumn?.readSwitchStatus === ReportReadSwitchStatus.ALL) {
          return true;
        }

        if (report.reportId === activeReportData?.reportId) {
          return true;
        }

        return report.readStatus === ReportReadStatus.UNREAD;
      })
      .sort((a, b) => {
        const dateAField =
          a.reportSourceType === ReportSourceType.LOOKOUT
            ? a.modifiedDatetime
            : a.publishedDatetime;
        const dateBField =
          b.reportSourceType === ReportSourceType.LOOKOUT
            ? b.modifiedDatetime
            : b.publishedDatetime;

        const dateA = new Date(dateAField).getTime();
        const dateB = new Date(dateBField).getTime();

        return dateB - dateA;
      });
  }
);

export const getMulticolumnReportsReadSwitchStatusSelector = createSelector(
  [reportsSelector, (_state, focusId: number) => focusId],
  (state, focusId) =>
    state.multicolumnsList.find(column => column.id === focusId)?.readSwitchStatus ??
    ReportReadSwitchStatus.UNREAD
);
